import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { usePermissions } from '../../hooks/usePermissions';

interface WoBulkUpdateBarProps {
  selectedCount: number;
  /** Users who may be named Tested By (role-filtered, RBAC.md) */
  checkerNames: string[];
  /** Users who may be named Reviewed/Approved By (role-filtered, RBAC.md) */
  approverNames: string[];
  onApply: (field: string, value: string) => void;
}

interface BulkField {
  field: string;
  label: string;
  type: 'select' | 'date';
  /** Which signoff step this field belongs to (drives role gating + name list) */
  step: 'check' | 'review' | 'approve';
}

const BULK_FIELDS: BulkField[] = [
  { field: 'CheckedBy', label: 'Tested By', type: 'select', step: 'check' },
  { field: 'TestDate', label: 'Test Date', type: 'date', step: 'check' },
  { field: 'ReviewedBy', label: 'Reviewed By', type: 'select', step: 'review' },
  { field: 'ReviewDate', label: 'Review Date', type: 'date', step: 'review' },
  { field: 'ApprovedBy', label: 'Approved By', type: 'select', step: 'approve' },
  { field: 'ApprovalDate', label: 'Approval Date', type: 'date', step: 'approve' },
];

export const WoBulkUpdateBar = ({ selectedCount, checkerNames, approverNames, onApply }: WoBulkUpdateBarProps) => {
  const theme = useTheme();
  // Offer only the signoff fields the caller's role may set (mirrors the
  // server-side validateBulkSignoffPermission gate). RBAC Phase C/D:
  // skill-based review is per-row, so BULK review is admin-only, as is the
  // approval pair (recorded on behalf of the designate).
  const { canCheckTest, isAdmin } = usePermissions();
  const allowedFields = BULK_FIELDS.filter((f) =>
    f.step === 'check' ? canCheckTest : isAdmin,
  );
  const [selectedField, setSelectedField] = useState<BulkField | null>(null);
  const [value, setValue] = useState('');
  const nameOptions = selectedField?.step === 'check' ? checkerNames : approverNames;

  const handleApply = () => {
    if (!selectedField || !value) return;
    onApply(selectedField.field, value);
    setValue('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.divider}`,
        borderTop: 'none',
        position: 'fixed',
        top: 64,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        borderRadius: '0 0 12px 12px',
        animation: 'bulkBarSlideDown 0.25s ease-out',
        '@keyframes bulkBarSlideDown': {
          '0%': { transform: 'translateX(-50%) translateY(-100%)', opacity: 0 },
          '100%': { transform: 'translateX(-50%) translateY(0)', opacity: 1 },
        },
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
        Bulk Update ({selectedCount} selected):
      </Typography>

      <Autocomplete
        size="small"
        options={allowedFields}
        getOptionLabel={(opt) => opt.label}
        value={selectedField}
        onChange={(_e, val) => {
          setSelectedField(val);
          setValue('');
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select field..." />
        )}
        sx={{ width: 180 }}
        disableClearable={false}
      />

      {selectedField?.type === 'select' && (
        <TextField
          size="small"
          select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{ width: 180 }}
        >
          {nameOptions.map((name) => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </TextField>
      )}

      {selectedField?.type === 'date' && (
        <TextField
          size="small"
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
      )}

      <Button
        variant="contained"
        size="small"
        disabled={!selectedField || !value}
        onClick={handleApply}
        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 80 }}
      >
        Apply
      </Button>
    </Box>
  );
};
